import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema } from "../lib/seo";

const Support = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToContact = (e) => {
    e.preventDefault();
    navigate("/");
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById("demo");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 50);
      }
    };
    setTimeout(() => tryScroll(0), 50);
  };

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/support")}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Support", path: "/support" },
        ])}
      />
      {/* Page Header */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/"
            onClick={handleBackToContact}
            className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <p className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Customer Support
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            We're Here to Help
          </h1>
        </div>
      </section>

      {/* Resource Sections */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Product Documentation */}
          <div className="bg-[#37464F]/50 border border-gray-800 rounded-lg p-8 hover:border-[#009688]/50 transition-all flex flex-col">
            <h2 className="text-2xl font-bold text-white mb-4">Product Documentation</h2>
            <p className="text-gray-300 mb-6 flex-1">
              Search our product documentation that includes a getting started guide, video tutorials, and more.
            </p>
            <a
              href="https://wristcoin.notion.site/wristcoin/5041f8ac1a4d465f95ecf56a13f549a1?v=ab571931b791476ab9b187f7ff7e5bb8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#009688] font-semibold hover:underline text-sm"
              data-testid="read-product-doc-link"
            >
              Read Product Doc
              <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </div>

          {/* Video Tutorials */}
          <div className="bg-[#37464F]/50 border border-gray-800 rounded-lg p-8 hover:border-[#009688]/50 transition-all flex flex-col">
            <h2 className="text-2xl font-bold text-white mb-4">Video Tutorials</h2>
            <p className="text-gray-300 mb-6 flex-1">
              Explore our comprehensive video tutorials by simply clicking the button below for an enriching learning experience.
            </p>
            <a
              href="https://www.youtube.com/@wristcoin/playlists"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#009688] font-semibold hover:underline text-sm"
              data-testid="view-tutorials-link"
            >
              View Tutorials
              <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </div>

          {/* API Documentation */}
          <div className="bg-[#37464F]/50 border border-gray-800 rounded-lg p-8 hover:border-[#009688]/50 transition-all flex flex-col">
            <h2 className="text-2xl font-bold text-white mb-4">API Documentation</h2>
            <p className="text-gray-300 mb-6 flex-1">
              Unlock limitless possibilities with WristCoin's API, seamlessly integrating with your systems to enhance efficiency and innovation.
            </p>
            <a
              href="https://wristcoin.notion.site/WristCoin-API-Reference-5dfab7ca4c00482a937f12703a77afed#2c2b080ee9ff48f38ef4611c9ff0b5e7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#009688] font-semibold hover:underline text-sm"
              data-testid="view-api-doc-link"
            >
              View API Doc
              <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Contact our support team */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Contact our support team
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            One-on-one calls with our support team are available for any event organizers, venues or companies using WristCoin. We are here to help every step of the way, contact us at <a href="mailto:support@mywristcoin.com" className="text-[#009688] font-semibold hover:underline" data-testid="support-email-link">support@mywristcoin.com</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Support;
